import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import useWebSocket from './useWebSocket';
import { shouldAllowNotification } from '../constants/notificationPreferences';

const useNotificationWebSocket = () => {
  const { user, notificationPreferences } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const {
    isConnected,
    subscribeToNotifications,
    unsubscribeFromNotifications,
    error: wsError
  } = useWebSocket();

  // Handle notifications from WebSocket
  const handleNotification = useCallback((notificationData) => {
    if (!shouldAllowNotification(notificationPreferences, notificationData?.type)) {
      return;
    }
    
    console.log('🔔 Notification received:', notificationData);
    
    setNotifications(prev => {
      const newNotifications = [notificationData, ...prev];
      return newNotifications.slice(0, 50); // Keep only last 50 notifications
    });
    
    // Update unread count
    setUnreadCount(prev => prev + 1);
    
    // Show browser notification if permission granted
    if (Notification.permission === 'granted' && notificationData.title) {
      new Notification(notificationData.title, {
        body: notificationData.message,
        icon: '/favicon.ico',
        tag: notificationData.id || 'receevit-notification'
      });
    }
  }, [notificationPreferences]);

  // Request notification permission
  useEffect(() => {
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Subscribe to notifications when connected
  useEffect(() => {
    if (isConnected && user && !isSubscribed) {
      // Add a small delay to ensure WebSocket is fully ready
      const timeoutId = setTimeout(() => {
        subscribeToNotifications();
        setIsSubscribed(true);
        
        // Set up global handler for notifications
        window.notificationHandler = handleNotification;
      }, 200);

      return () => {
        clearTimeout(timeoutId);
      };
    }

    return () => {
      if (isSubscribed) {
        unsubscribeFromNotifications();
        setIsSubscribed(false);
        window.notificationHandler = null;
      }
    };
  }, [isConnected, user, isSubscribed, subscribeToNotifications, unsubscribeFromNotifications, handleNotification]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      window.notificationHandler = null;
    };
  }, []);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, isRead: true }
          : notification
      )
    );
    
    // Update unread count
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, isRead: true }))
    );
    setUnreadCount(0);
  }, []);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  return {
    notifications,
    unreadCount,
    isConnected,
    isSubscribed,
    error: wsError,
    markAsRead,
    markAllAsRead,
    clearAll
  };
};

export default useNotificationWebSocket;
